import { api } from '../api.js';
import { humanTime, ago } from '../ui.js';
import { Terminal } from '../terminal.js';
import { agent, setView, refresh, pollState, term, closeTerminal } from '../agent-store.js';

// Раздел «Агент»: где он работает, в каком состоянии и как его туда поставить.
//
// Состояние живёт в общем хранилище, а не здесь: уход на другую вкладку не
// должен ни сбрасывать выбор, ни ронять открытый терминал, ни показывать
// секунду неправды, пока всё грузится заново.

export const AgentSection = {
  components: { Terminal },
  data() {
    return {
      showForm: false,
      form: {
        host: '', port: 22, login: 'root',
        auth: 'password', password: '', privateKey: '', passphrase: '',
        proxyMode: 'off', key: '', proxy: '', installDocker: false,
      },
      busy: false,
      again: false,
      log: null,
      history: [],
      historyFor: 0,
      termFor: 0,
      copied: '',
      showTokens: false,
      timer: null,
      stateTimer: null,
      tick: 0,
    };
  },
  computed: {
    /** Серверы, отставшие от сайта: про них и говорит уведомление. */
    outdated() { return this.s.hosts.filter((h) => h.stale); },
    s() { return agent; },
    view() { return agent.view; },
    loaded() { return agent.loaded; },
    online() { return Boolean(agent.state && agent.state.online); },
    /**
     * Агенты того места, на которое смотришь. Раньше список был один на двоих:
     * переключаешь на сервер, серверов нет ни одного, а под переключателем всё
     * те же Claude и Codex с ноутбука — и отличить это было нельзя.
     */
    agents() {
      const st = agent.state || {};
      return (this.view === 'server' ? st.agents_server : st.agents_local) || [];
    },
    activeHost() { return agent.hosts.find((h) => h.status === 'ok') || null; },

    /** Порог свежести — с сервера: судить о ней по своему правилу нельзя. */
    onlineMs() { return agent.state?.online_ms || 45000; },

    /** Статус того места, которое сейчас смотрят: про чужое врать незачем. */
    runnerLine() {
      if (!agent.loaded) return { state: 'wait', text: 'проверяю…' };
      const here = agent.place === agent.view;
      if (!here) {
        return {
          state: 'off',
          text: agent.view === 'local'
            ? 'Локальный раннер сейчас не обслуживается: агент работает на сервере'
            : 'Раннер на сервере сейчас не обслуживается: агент работает на компьютере',
        };
      }
      if (this.online) {
        return {
          state: 'up',
          text: agent.view === 'local' ? 'Локальный раннер на связи' : 'Раннер на сервере на связи',
        };
      }
      return {
        state: 'down',
        text: agent.view === 'local'
          ? 'Локальный раннер не запущен'
          : 'Раннер на сервере молчит',
      };
    },

    /**
     * Команда для локального запуска. Одна и та же на все случаи: раннера ещё
     * нет, раннер есть и устарел, раннер есть и свежий.
     *
     * Раньше это был `git clone` в текущий каталог, и второй запуск упирался в
     * «destination path already exists» — а из какого каталога человек
     * копировал команду в прошлый раз, он не помнит и помнить не должен.
     * Теперь место фиксированное, обновление встроено, и команду можно просто
     * держать в буфере обмена.
     */
    localCommand() {
      const token = agent.fresh?.token || '<токен>';
      const repo = agent.options.repo || 'https://gitlab.sigmagoy.fun/root/hh-runner.git';
      return [
        '# один и тот же текст: поставит, обновит и запустит',
        'D=/opt/hh-runner; mkdir -p "$D" 2>/dev/null || D=$HOME/hh-runner; mkdir -p "$D"',
        `[ -d "$D/.git" ] || git clone -q ${repo} "$D"`,
        'git -C "$D" fetch -q origin && git -C "$D" reset -q --hard origin/main',
        'cd "$D" && npm install --omit=dev --no-audit --no-fund --silent',
        `printf 'HH_URL=%s\\nHH_TOKEN=%s\\n' '${location.origin}' '${token}' > .env`,
        'npm start',
      ].join('\n');
    },
  },
  async created() {
    if (!agent.loaded) await refresh(); else refresh();
    // Живой индикатор: секунды здесь важнее экономии запросов.
    this.stateTimer = setInterval(async () => {
      await pollState();
      this.tick += 1;   // «приходил N секунд назад» должно идти само
    }, 3000);
    const running = agent.hosts.find((h) => h.running);
    if (running) this.watch(running.id);
    if (term.open) this.termFor = term.hostId;
  },
  beforeUnmount() {
    clearInterval(this.timer);
    clearInterval(this.stateTimer);
  },
  methods: {
    humanTime,
    ago(iso) { return this.tick >= 0 ? ago(iso) : ''; },
    setView,

    async setPlace(place) {
      setView(place);
      agent.error = '';
      if (place === agent.place) return;
      if (place === 'server' && !this.activeHost) return;
      try {
        const r = await api.put('/api/agent-server/place', { place });
        agent.place = r.place;
        await pollState();
      } catch (e) {
        agent.error = e.message;
      }
    },

    /** Обновить раннер на сервере — свежим кодом, тем же токеном. */
    update(host) {
      // Доступ сохранён — обновляем молча. Нет (сервер ставили раньше, чем это
      // появилось) — спрашиваем, как раньше, и заодно сохраняем на будущее.
      if (!host.has_auth) return this.openForm(host);
      this.busy = true;
      agent.error = '';
      return api.post(`/api/agent-server/hosts/${host.id}/update`, {})
        .then(() => { this.watch(host.id); return refresh(); })
        .catch((e) => { agent.error = e.message; })
        .finally(() => { this.busy = false; });
    },

    /**
     * Форма установки.
     *
     * Ключ прокси подставляем здесь, а не при загрузке: настройки приезжают
     * отдельным запросом, и на втором сервере поле оказывалось пустым — форму
     * открывали раньше, чем они доехали.
     *
     * С сервером — переустановка того же: адрес и пользователя подставляем.
     * Доступ спрашиваем только у тех серверов, что ставились до того, как он
     * стал храниться; остальные обновляются кнопкой без вопросов.
     */
    openForm(host = null) {
      if (!this.form.key.trim()) this.form.key = agent.options.key || '';
      if (host) {
        this.form.host = host.host;
        this.form.port = host.port;
        this.form.login = host.login;
        this.again = true;
      } else {
        this.again = false;
      }
      this.showForm = true;
    },

    async deployHost() {
      const byKey = this.form.auth === 'key';
      if (!this.form.host.trim() || (byKey ? !this.form.privateKey.trim() : !this.form.password)) {
        agent.error = byKey ? 'нужен адрес и приватный ключ' : 'нужен адрес и пароль';
        return;
      }
      if (this.form.proxyMode === 'key' && !this.form.key.trim()) {
        agent.error = 'включён прокси по ключу, но ключ не задан';
        return;
      }
      if (this.form.proxyMode === 'address' && !this.form.proxy.trim()) {
        agent.error = 'включён готовый прокси, но адрес не задан';
        return;
      }
      this.busy = true;
      agent.error = '';
      try {
        const { host } = await api.post('/api/agent-server/hosts', {
          ...this.form,
          proxy_mode: this.form.proxyMode,
          install_docker: this.form.installDocker,
          password: byKey ? '' : this.form.password,
          private_key: byKey ? this.form.privateKey : '',
          passphrase: byKey ? this.form.passphrase : '',
        });
        this.form.password = '';
        this.form.privateKey = '';
        this.form.passphrase = '';
        this.showForm = false;
        await refresh();
        this.watch(host.id);
      } catch (e) {
        agent.error = e.message;
      } finally {
        this.busy = false;
      }
    },

    watch(hostId) {
      clearInterval(this.timer);
      const step = async () => {
        try {
          this.log = await api.get(`/api/agent-server/hosts/${hostId}/log`);
          this.scrollLog();
          if (this.log.done) {
            clearInterval(this.timer);
            this.timer = null;
            await refresh();
          }
        } catch (e) {
          clearInterval(this.timer);
          this.timer = null;
          this.log = {
            ...(this.log || { events: [] }),
            done: true,
            error: `связь с сайтом прервалась (${e.message}). Что стало с установкой, `
              + 'отсюда не видно — обнови страницу',
          };
        }
      };
      this.timer = setInterval(step, 1500);
      step();
    },

    scrollLog() {
      this.$nextTick(() => {
        const el = this.$refs.console;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    async showHistory(hostId) {
      if (this.historyFor === hostId) { this.historyFor = 0; return; }
      const { rows } = await api.get(`/api/agent-server/hosts/${hostId}/runs`);
      this.history = rows;
      this.historyFor = hostId;
    },

    async openRun(runId) {
      const { run } = await api.get(`/api/agent-server/runs/${runId}`);
      this.log = { run_id: run.id, events: run.events, done: true, error: run.error };
      this.scrollLog();
    },

    toggleTerm(hostId) {
      if (this.termFor === hostId) {
        // Открытую сессию закрываем явно: свернуть её мышью — не то же самое,
        // что оборвать соединение с чужим сервером.
        if (term.open && term.hostId === hostId) closeTerminal();
        this.termFor = 0;
        return;
      }
      this.termFor = hostId;
    },

    /**
     * Сделать сервер рабочим. Серверов бывает несколько, а работать должен
     * один: иначе задание достаётся тому, кто успел первым, и понять, откуда
     * шла работа, невозможно.
     */
    async makeActive(host) {
      try {
        await api.put(`/api/agent-server/hosts/${host.id}/active`, {});
        await refresh();
        await pollState();
      } catch (e) {
        agent.error = e.message;
      }
    },

    async removeHost(host) {
      const secret = prompt(
        `Снять раннер с ${host.host}?\n\n`
        + 'Введи пароль или вставь приватный ключ — остановлю контейнер и уберу '
        + 'каталог. Оставишь пустым — уберу только запись здесь, а контейнер там '
        + 'продолжит работать.');
      if (secret === null) return;
      const byKey = /BEGIN [A-Z ]*PRIVATE KEY/.test(secret);
      try {
        const { stopped } = await api.post(`/api/agent-server/hosts/${host.id}/remove`,
          byKey ? { private_key: secret } : { password: secret });
        if (stopped) this.watch(host.id); else await refresh();
      } catch (e) {
        agent.error = e.message;
      }
    },

    async issueToken(name) {
      const label = name || prompt('Для чего этот токен? (например: ноутбук)');
      if (label === null) return;
      try {
        agent.fresh = await api.post('/api/agent-server/tokens', { name: label || '' });
        await refresh();
      } catch (e) {
        agent.error = e.message;
      }
    },

    async revokeToken(t) {
      if (!confirm(`Отозвать токен «${t.name || t.tail}»? Раннер, который им пользуется, встанет.`)) return;
      await api.del(`/api/agent-server/tokens/${t.id}`);
      if (agent.fresh && agent.fresh.id === t.id) agent.fresh = null;
      await refresh();
      await pollState();
    },

    async copy(text, what) {
      try {
        await navigator.clipboard.writeText(text);
        this.copied = what;
        setTimeout(() => { this.copied = ''; }, 2000);
      } catch { /* без буфера обмена текст всё равно виден и выделяется */ }
    },

    /** Свежая ли отметка. Одна проверка на весь экран, а не своя в каждом месте. */
    live(at) {
      return Boolean(at) && Date.now() - new Date(at).getTime() < this.onlineMs;
    },

    statusWord(h) {
      return { ok: 'установлен', pending: 'ставится', failed: 'не поставился' }[h.status] || h.status;
    },
  },
  template: `
    <div>
      <p class="err" v-if="s.error">{{ s.error }}</p>

      <!-- Вход агента в подписку: единственное, ради чего раньше приходилось
           заходить на сервер. -->
      <div class="card login-card" v-if="s.state && s.state.login">
        <h3 style="margin-top:0">Агент ждёт входа в подписку</h3>
        <p class="note" style="margin-top:0">
          Открой ссылку, войди в свою учётную запись и введи код. Заходить на
          сервер не нужно — как только подтвердишь, раннер продолжит сам.
        </p>
        <p style="margin:0 0 14px">
          <a :href="s.state.login.url" target="_blank" rel="noopener" class="btn primary">
            Открыть страницу входа →
          </a>
        </p>
        <div class="code-row">
          <code class="code-big">{{ s.state.login.code }}</code>
          <button class="ghost sm" @click="copy(s.state.login.code, 'code')">
            {{ copied === 'code' ? 'скопирован' : 'скопировать' }}
          </button>
        </div>
      </div>

      <!-- ── где работает ──────────────────────────────────────────────── -->
      <div class="seg wide" style="margin-bottom:14px">
        <button :class="{ on: view === 'local' }" @click="setPlace('local')">
          На моём компьютере
        </button>
        <button :class="{ on: view === 'server' }" @click="setPlace('server')">
          На сервере
        </button>
      </div>

      <!-- Состояние: пока не загрузились, ничего не утверждаем. -->
      <div class="card status-card">
        <div class="live" :class="runnerLine.state">
          <i></i>
          <span style="flex:1">{{ runnerLine.text }}</span>
          <span class="faint" v-if="loaded && online && s.state.seen_at" :title="humanTime(s.state.seen_at)">
            {{ ago(s.state.seen_at) }}
          </span>
        </div>

        <div class="agent-rows" v-if="loaded && agents.length">
          <div class="arow" v-for="a in agents" :key="a.id">
            <span class="chk" :class="!a.version ? 'down' : (a.logged_in ? 'up' : 'wait')"><i></i></span>
            <b style="flex:1">{{ a.name }}</b>
            <span class="faint" v-if="!a.version">не установлен</span>
            <span class="faint" v-else-if="a.logged_in">{{ a.version }} · вход выполнен</span>
            <span class="warnfg" v-else>{{ a.version }} · вход не выполнен</span>
          </div>
        </div>
        <p class="faint" v-else-if="loaded && view === 'server' && !s.hosts.length"
           style="margin:12px 0 0">
          Серверов пока нет. Что на них стоит, рассказывает раннер оттуда —
          добавь сервер ниже, и здесь появятся его агенты.
        </p>
        <p class="faint" v-else-if="loaded && view === 'server'" style="margin:12px 0 0">
          Раннер с сервера ещё не приходил: про его агентов пока ничего не
          известно. Он расскажет, когда возьмёт первое задание.
        </p>
        <p class="faint" v-else-if="loaded" style="margin:12px 0 0">
          Про агентов пока ничего не известно: это рассказывает раннер, когда
          приходит за заданием.
        </p>
      </div>

      <!-- ── на своей машине ───────────────────────────────────────────── -->
      <template v-if="view === 'local'">
        <h2>Как запустить у себя</h2>
        <div class="card">
          <p class="note" style="margin-top:0">
            Одна команда вместо пяти шагов: заберёт приложение, положит настройки
            и запустит раннер. Нужны git, node и установленный агент — вход в
            подписку подтвердишь здесь же, кодом.
            <b>Её можно просто держать в буфере и вставлять снова</b>: раннер
            всегда ставится в <code>/opt/hh-runner</code>, а повторный запуск
            подтягивает свежую версию. Каталог, из которого ты её вставляешь,
            значения не имеет.
          </p>

          <template v-if="s.fresh">
            <pre class="deploy-log">{{ localCommand }}</pre>
            <div class="row" style="margin-top:10px">
              <button class="primary sm" @click="copy(localCommand, 'cmd')">
                {{ copied === 'cmd' ? 'скопировано' : 'Скопировать команду' }}
              </button>
            </div>
            <p class="faint" style="margin-top:10px">
              В команде подставлен токен — он виден один раз, поэтому скопируй
              сейчас.
            </p>
          </template>

          <div class="row" v-else>
            <button class="primary" @click="issueToken('эта машина')">
              Собрать команду для этой машины
            </button>
          </div>
        </div>
      </template>

      <!-- ── на сервере ────────────────────────────────────────────────── -->
      <template v-else>
        <h2>Серверы</h2>

        <p class="note warn" v-if="outdated.length" style="margin-top:0">
          <b>Вышла новая версия {{ s.releaseNow }}.</b>
          {{ outdated.length === 1 ? 'Сервер' : 'Серверы' }}
          {{ outdated.map((h) => h.host).join(', ') }}
          {{ outdated.length === 1 ? 'работает' : 'работают' }} на старой.
          Обновление — одна кнопка, собес при этом не прервётся: контейнер
          меняется после того, как соберётся новый.
        </p>

        <p class="note" v-if="s.hosts.length > 1" style="margin-top:0">
          Работать должен один сервер: задания идут только на тот, что отмечен
          рабочим. Остальные ждут и в очередь не лезут.
        </p>

        <div class="card" v-if="loaded && !s.hosts.length">
          <p class="muted" style="margin:0">
            Пока ни одного. Добавь сервер — раннер уедет туда, и ноутбук будет
            не нужен.
          </p>
        </div>

        <div class="card host" v-for="h in s.hosts" :key="h.id">
          <div class="row">
            <div style="flex:1;min-width:0">
              <b>{{ h.login }}@{{ h.host }}<template v-if="h.port !== 22">:{{ h.port }}</template></b>
              <span class="tag ok" v-if="h.active" style="margin-left:8px">рабочий</span>
              <span class="tag warn" v-if="h.stale" style="margin-left:8px">есть обновление</span>
              <div class="faint" v-if="h.deployed_at">
                поставлен {{ humanTime(h.deployed_at) }}<template v-if="h.version">,
                версия {{ h.version }}<template v-if="h.stale"> — на сайте уже
                {{ s.versionNow }}</template></template>
              </div>
            </div>
            <button class="ghost sm" @click="makeActive(h)" v-if="!h.active">
              Сделать рабочим
            </button>
            <button class="ghost sm" @click="watch(h.id)" v-if="h.running">Ход установки</button>
            <button class="ghost sm" :class="{ on: h.stale }" @click="update(h)"
                    v-if="!h.running" :disabled="busy"
                    :title="h.has_auth
                      ? 'Соберёт контейнер заново из свежего кода. Доступ уже сохранён'
                      : 'Доступ не сохранён — спросим пароль'">
              {{ h.stale || !h.version ? 'Обновить' : 'Переустановить' }}
            </button>
            <button class="ghost sm" :class="{ on: termFor === h.id }" @click="toggleTerm(h.id)">
              Терминал
            </button>
            <button class="ghost sm" @click="showHistory(h.id)">
              {{ historyFor === h.id ? 'Скрыть историю' : 'История' }}
            </button>
            <button class="ghost sm" @click="removeHost(h)">Снять</button>
          </div>

          <div class="checks">
            <span class="chk" :class="h.status === 'ok' ? 'up' : (h.status === 'pending' ? 'wait' : 'down')">
              <i></i>Установка: {{ statusWord(h) }}
            </span>
            <span class="chk" :class="live(h.last_seen_at) ? 'up' : (h.last_seen_at ? 'wait' : 'down')"
                  :title="h.last_seen_at ? humanTime(h.last_seen_at) : 'ни разу не приходил'">
              <i></i>Раннер:
              <template v-if="live(h.last_seen_at)">на связи</template>
              <template v-else-if="h.last_seen_at">молчит, последний раз {{ ago(h.last_seen_at) }}</template>
              <template v-else>ни разу не приходил</template>
            </span>
          </div>

          <p class="faint" v-if="h.note && h.status === 'failed'" style="margin:10px 0 0">
            {{ h.note }}
          </p>

          <Terminal v-if="termFor === h.id" :host="h" @close="termFor = 0"></Terminal>

          <div class="runs" v-if="historyFor === h.id">
            <div class="run-row" v-for="r in history" :key="r.id" @click="openRun(r.id)">
              <span class="chk" :class="r.status === 'ok' ? 'up' : (r.status === 'running' ? 'wait' : 'down')"><i></i></span>
              <span style="flex:1">
                {{ r.kind === 'remove' ? 'снятие' : 'установка' }} · {{ humanTime(r.started_at) }}
              </span>
              <span class="faint">{{ r.steps }} шагов</span>
            </div>
            <p class="faint" v-if="!history.length" style="margin:8px 0 0">Установок ещё не было.</p>
          </div>
        </div>

          <div class="row" style="margin-top:12px" v-if="!showForm">
          <button class="primary" @click="openForm()">+ Добавить сервер</button>
        </div>

        <template v-if="showForm">
          <h2>{{ again ? 'Переустановка' : 'Куда ставить' }}</h2>
          <div class="card">
            <p class="note" style="margin-top:0" v-if="again">
              Тот же сервер: каталог, токен и запись о нём остаются те же,
              а код раннера уезжает свежий и контейнер пересобирается — так и
              обновляется приложение на сервере. Логин агента переустановку
              переживает, заново входить не придётся. Доступ сохранится
              зашифрованным, и дальше обновление будет одной кнопкой.
            </p>
            <p class="note" style="margin-top:0" v-else>
              Сервер может быть рабочим: раннер живёт в контейнере, из которого
              нет ни хостовой файловой системы, ни docker-сокета, ни повышения
              привилегий. Ставится в <code>/opt/hh-runner</code>, ничего чужого
              не трогает. Нужен доступ рутом или пользователем с sudo.
            </p>

            <div class="row wrap" style="gap:10px;margin-top:14px">
              <div style="flex:2;min-width:200px">
                <label>Адрес</label>
                <input v-model="form.host" placeholder="203.0.113.10 или server.example.com">
              </div>
              <div style="flex:0 0 90px">
                <label>Порт</label>
                <input v-model.number="form.port">
              </div>
              <div style="flex:1;min-width:120px">
                <label>Пользователь</label>
                <input v-model="form.login" placeholder="root">
              </div>
            </div>

            <div class="seg" style="margin-top:14px">
              <button :class="{ on: form.auth === 'password' }" @click="form.auth = 'password'">
                Пароль
              </button>
              <button :class="{ on: form.auth === 'key' }" @click="form.auth = 'key'">
                Приватный ключ
              </button>
            </div>

            <div class="row wrap" style="gap:10px;margin-top:10px" v-if="form.auth === 'password'">
              <div style="flex:1;min-width:200px">
                <label>Пароль</label>
                <input type="password" v-model="form.password"
                       placeholder="уйдёт один раз и не сохранится">
              </div>
            </div>

            <div v-else style="margin-top:10px">
              <label>Приватный ключ</label>
              <textarea v-model="form.privateKey" rows="4" class="key" id="pk"
                        placeholder="-----BEGIN OPENSSH PRIVATE KEY----- … -----END-----"></textarea>
              <div class="row" style="margin-top:10px">
                <div style="flex:1;min-width:200px">
                  <label>Пароль к ключу <i class="faint">если он зашифрован</i></label>
                  <input type="password" v-model="form.passphrase" placeholder="обычно пусто">
                </div>
              </div>
              <p class="faint" style="margin-top:6px">
                Под не-рутом нужен sudo без пароля: при входе по ключу пароль
                для sudo взять неоткуда.
              </p>
            </div>

            <label class="check" style="margin-top:16px">
              <input id="install-docker" type="checkbox" v-model="form.installDocker">
              <span>
                Поставить docker, если его нет
                <i class="faint">официальным скриптом с сайта docker</i>
              </span>
            </label>

            <label class="check">
              <input id="proxy-on" type="checkbox"
                     :checked="form.proxyMode !== 'off'"
                     @change="form.proxyMode = $event.target.checked ? 'key' : 'off'">
              <span>
                Агент ходит через прокси
                <i class="faint">нужно там, где модель не отвечает с адресов сервера</i>
              </span>
            </label>

            <template v-if="form.proxyMode !== 'off'">
              <div class="seg" style="margin-top:12px">
                <button :class="{ on: form.proxyMode === 'key' }" @click="form.proxyMode = 'key'">
                  Поднять по ключу
                </button>
                <button :class="{ on: form.proxyMode === 'address' }" @click="form.proxyMode = 'address'">
                  Уже есть на сервере
                </button>
              </div>

              <div style="margin-top:10px" v-if="form.proxyMode === 'key'">
                <label>Ключ</label>
                <textarea v-model="form.key" rows="2" class="key" id="vpnkey"
                          placeholder="vless://… · hysteria2://… · trojan://… · ss://…"></textarea>
                <p class="faint" style="margin-top:6px">
                  Понимаем {{ s.options.schemes.join(', ') }}.
                </p>
              </div>

              <div style="margin-top:10px" v-else>
                <label>Адрес прокси</label>
                <input v-model="form.proxy" id="proxyaddr"
                       placeholder="http://host.docker.internal:10809">
              </div>
            </template>

            <div class="row" style="margin-top:16px">
              <button class="primary" @click="deployHost" :disabled="busy">
                {{ busy ? 'Подключаюсь…' : (again ? 'Переустановить' : 'Развернуть раннер') }}
              </button>
              <button class="ghost" @click="showForm = false">Отмена</button>
            </div>
          </div>
        </template>
      </template>

      <!-- ── ход установки ─────────────────────────────────────────────── -->
      <template v-if="log">
        <h2>Ход установки</h2>
        <div class="card">
          <div class="row" style="margin-bottom:10px">
            <span class="tag" v-if="!log.done">
              идёт<template v-if="log.since"> · {{ log.since }}с</template>
            </span>
            <span class="tag bad" v-else-if="log.error">не вышло</span>
            <span class="tag ok" v-else>готово</span>
            <span class="spacer"></span>
            <button class="ghost sm" @click="log = null">Закрыть</button>
          </div>

          <div class="console" ref="console">
            <template v-for="(e, i) in log.events" :key="i">
              <div class="cstep" :class="e.state" v-if="e.kind === 'step'">
                <i></i>{{ e.text }}
              </div>
              <div class="cout" v-else>{{ e.text }}</div>
            </template>
            <p class="faint" v-if="!log.events.length" style="margin:0">подключаюсь…</p>
          </div>

          <p class="err" v-if="log.error">{{ log.error }}</p>
        </div>
      </template>

      <!-- ── токены ────────────────────────────────────────────────────── -->
      <h2 class="fold" @click="showTokens = !showTokens">
        Токены раннера
        <span class="faint">{{ showTokens ? 'свернуть' : s.tokens.length + ' шт.' }}</span>
      </h2>

      <div class="card" v-show="showTokens">
        <p class="note" style="margin-top:0">
          Токен — пропуск раннера к заданиям. Он заменяет пароль от сайта и
          отзывается одной кнопкой.
        </p>

        <div class="card" v-if="s.fresh" style="border-color:var(--ok-line);background:var(--ok-bg)">
          <p style="margin:0 0 8px">Токен виден один раз. Скопируй сейчас:</p>
          <div class="code-row">
            <code class="code-token">{{ s.fresh.token }}</code>
            <button class="ghost sm" @click="copy(s.fresh.token, 'token')">
              {{ copied === 'token' ? 'скопирован' : 'скопировать' }}
            </button>
          </div>
        </div>

        <div class="row token-row" v-for="t in s.tokens" :key="t.id">
          <div style="flex:1;min-width:0">
            <b>{{ t.name || 'без имени' }}</b> <span class="faint">…{{ t.tail }}</span>
            <div class="faint">
              создан {{ humanTime(t.created_at) }}
            </div>
            <div class="faint" v-if="t.last_seen_at">
              приходил {{ humanTime(t.last_seen_at) }}<template v-if="t.last_ip"> · {{ t.last_ip }}</template>
            </div>
          </div>
          <button class="ghost sm" @click="revokeToken(t)">Отозвать</button>
        </div>

        <div class="row" style="margin-top:12px">
          <button class="ghost sm" @click="issueToken()">+ Выпустить токен</button>
        </div>
      </div>
    </div>`,
};
